import React from 'react';
import { Box, CircularProgress, MenuItem, Select, SelectProps, TextField, useTheme } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { SaveStatus, UomOption } from '../interfaces/ItemWasteChecksheetData';

interface QuantityInputWithUomProps {
  id: string;
  value: number | string;
  disabled: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  label: string | null;
  setWidth: boolean;
  status: SaveStatus;
  uom?: string;
  availableUoms?: UomOption[];
  onUomChange?: NonNullable<SelectProps<string>['onChange']>;
  showUomSelect: boolean;
}

const QuantityInputWithUom = ({
  id,
  value,
  disabled,
  onChange,
  label,
  setWidth,
  status,
  uom,
  availableUoms,
  onUomChange,
  showUomSelect,
}: QuantityInputWithUomProps) => {
  const theme = useTheme();
  const controlHeight = 40;

  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', minHeight: `${controlHeight}px` }}>
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        <TextField
          label={label}
          type="number"
          size="small"
          id={id}
          variant="outlined"
          margin="none"
          disabled={disabled || status === 'saving'}
          sx={{
            minWidth: setWidth ? '60px' : undefined,
            width: '100%',
            my: 0,
            ...(showUomSelect && {
              '& .MuiOutlinedInput-root': {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              },
            }),
            '& .MuiFormControl-root': {
              margin: 0,
            },
            '& .MuiInputBase-root': {
              alignItems: 'center',
              margin: 0,
              minHeight: `${controlHeight}px`,
            },
          }}
          value={value ?? ''}
          onChange={onChange}
          inputProps={{ min: 0 }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputProps: { sx: { textAlign: 'right', pr: 2 } },
          }}
        />
        {status === 'saving' && (
          <Box className='hide-on-print'
            sx={{ position: 'absolute', top: -4, right: -4 }}>
            <CircularProgress size={16} />
          </Box>
        )}
        {status === 'saved' && (
          <Box className='hide-on-print'
            sx={{ position: 'absolute', top: -4, right: -4, color: theme.palette.success.main }}>
            <CloudDoneIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
        {status === 'error' && (
          <Box className='hide-on-print'
            sx={{ position: 'absolute', top: -4, right: -4, color: theme.palette.error.main }}>
            <ErrorIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
        {status === 'idle' && (
          <Box className='hide-on-print'
            sx={{ position: 'absolute', top: -6, right: -6, color: theme.palette.warning.main }}>
            <WarningIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
      </Box>
      {showUomSelect && availableUoms && availableUoms.length >= 1 && (
        <Select
          size="small"
          value={uom ?? availableUoms[0]?.code ?? ''}
          onChange={onUomChange}
          disabled={disabled}
          sx={{
            minWidth: '50px',
            maxWidth: '58px',
            fontSize: '0.7rem',
            my: 0,
            minHeight: `${controlHeight}px`,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            '& .MuiInputBase-root': {
              margin: 0,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderLeft: 'none',
            },
            '& .MuiSelect-select': {
              alignItems: 'center',
              display: 'flex',
              minHeight: `${controlHeight}px`,
              py: 0,
            },
          }}
        >
          {availableUoms.map((u) => (
            <MenuItem key={u.code}
              value={u.code}
              sx={{ fontSize: '0.75rem' }}>
              {u.code}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
};

export default React.memo(QuantityInputWithUom, (prev, next) => {
  return (
    prev.id === next.id &&
    prev.value === next.value &&
    prev.disabled === next.disabled &&
    prev.status === next.status &&
    prev.label === next.label &&
    prev.setWidth === next.setWidth &&
    prev.uom === next.uom &&
    prev.showUomSelect === next.showUomSelect &&
    prev.availableUoms === next.availableUoms
  );
});
